import type { WorkspaceNode } from "@/_store/msp/msp-treeview.module.ts";
import {
  SubscriptionAddon,
  SubscriptionBundle,
  SubscriptionModule,
  SubscriptionsFilterType,
  SubscriptionType,
  WorkspaceType,
} from "@/constants/workplaces.ts";
import type { MspTreeviewFilters } from "@/_store/filters.module.ts";
import moment from "moment/moment";

/**
 * Pre-computed lookup structures for structured filters.
 * Built once per filtering pass by `buildFilterLookups` to avoid redundant
 * calculations (date math, array splitting, Set construction) inside the
 * per-node predicate which runs for every node in the tree.
 */
interface StructuredFilterLookups {
  /** O(1) lookup for plan filter instead of Array.includes(). */
  subscriptionTypeSet: Set<SubscriptionType>;
  /** O(1) lookup for label filter instead of Array.includes(). */
  labelSet: Set<string>;
  mspPolicyId: string;
  nearExpirationOnly: boolean;
  /** Pre-computed UTC cutoff — avoids calling moment.utc().add(7, "days") per node. */
  expirationCutoff: moment.Moment | null;
  /** Addon names pre-split by type into Sets for O(1) lookups. */
  selectedBundles: Set<string>;
  selectedModules: Set<string>;
  selectedAddons: Set<string>;
  hasAddonFilter: boolean;
}

/**
 * A generic internal helper to traverse a tree and collect IDs based on a condition.
 *
 * @param nodes - The nodes to begin traversing.
 * @param predicate - A function that returns true if the node's ID should be collected.
 * @returns An array of collected workspace IDs.
 */
function collectWorkspaceIds(
  nodes: WorkspaceNode[],
  predicate: (node: WorkspaceNode) => boolean
): string[] {
  const ids: string[] = [];

  const traverse = (items: WorkspaceNode[]) => {
    for (const node of items) {
      // Check the condition to see if we should collect this ID
      if (predicate(node)) {
        ids.push(node.workspaceId);
      }

      // Always continue traversing deeper if children exist
      if (node.children?.length) {
        traverse(node.children);
      }
    }
  };

  traverse(nodes);
  return ids;
}

/**
 * Flattens the workspace tree into a map for a quick lookup by `workspaceId`.
 *
 * @param nodes Workspace roots to traverse.
 * @returns A Map keyed by workspaceId pointing to each node in the tree.
 */
export function buildWorkspaceNodeMap(nodes: WorkspaceNode[]): Map<string, WorkspaceNode> {
  const map = new Map<string, WorkspaceNode>();

  const collectNodes = (items: WorkspaceNode[]) => {
    items.forEach((node) => {
      map.set(node.workspaceId, node);
      if (node.children?.length) {
        collectNodes(node.children);
      }
    });
  };

  collectNodes(nodes);

  return map;
}

/**
 * Collects ids for every node that has at least one child (i.e., expandable rows).
 * Uses the generic collector with a predicate checking for children.
 *
 * @param nodes Workspace roots to traverse.
 * @returns All parent workspace ids in the provided trees.
 */
export function getWorkspaceParentIds(nodes: WorkspaceNode[]): string[] {
  return collectWorkspaceIds(nodes, (node) => !!(node.children && node.children.length > 0));
}

/**
 * Returns all descendant ids (direct children and deeper) for the given node.
 * Uses the generic collector starting from the node's children, accepting all nodes.
 *
 * @param node Node whose subtree should be collected.
 * @returns Flattened list of descendant workspace ids.
 */
export function getWorkspaceDescendantIds(node: WorkspaceNode): string[] {
  if (!node.children || node.children.length === 0) {
    return [];
  }
  return collectWorkspaceIds(node.children, () => true);
}

/**
 * Resolves a list of workspace ids to their nodes using a pre-built map.
 *
 * @param ids Workspace identifiers to look up.
 * @param nodesMap Map produced by `buildWorkspaceNodeMap`.
 * @returns Nodes matching the provided ids (order preserved from `ids`).
 */
export function mapWorkspaceIdsToNodes(
  ids: string[],
  nodesMap: Map<string, WorkspaceNode>
): WorkspaceNode[] {
  const uniqueIds = Array.from(new Set(ids));

  return uniqueIds
    .map((id) => nodesMap.get(id))
    .filter((item): item is WorkspaceNode => Boolean(item));
}

/**
 * A generic helper to filter a workspace tree (or array of trees) recursively.
 * It retains a node if:
 * 1. The node itself matches the predicate.
 * 2. OR any of its descendants match the predicate (preserving the path to the match).
 *
 * Returns a filtered tree without mutating original nodes.
 * Uses structural sharing: unchanged subtrees reuse original node references.
 *
 * @param nodes - The root WorkspaceNode(s) of the tree(s).
 * @param predicate - A function returning true if the node matches the criteria.
 * @returns A new array containing the filtered root node(s).
 */
export function filterWorkspaceTree(
  nodes: WorkspaceNode[],
  predicate: (node: WorkspaceNode) => boolean
): WorkspaceNode[] {
  /**
   * Inner recursive function to traverse and filter a SINGLE node.
   */
  const filterNodeRecursively = (node: WorkspaceNode): WorkspaceNode | null => {
    // Filter children first (depth-first traversal)
    const filteredChildren: WorkspaceNode[] = (node.children || []).reduce(
      (acc: WorkspaceNode[], child: WorkspaceNode) => {
        const filteredChild = filterNodeRecursively(child);
        if (filteredChild) {
          acc.push(filteredChild);
        }
        return acc;
      },
      []
    );

    // Determine if we keep this node
    const isMatch = predicate(node);
    const hasMatchingChildren = filteredChildren.length > 0;

    if (isMatch || hasMatchingChildren) {
      // Structural sharing: if no children were pruned, return the original node reference
      // instead of creating a shallow copy. This avoids unnecessary object allocation
      // and helps Vue's reactivity skip re-rendering unchanged subtrees.
      const originalChildren = node.children ?? [];
      const childrenUnchanged =
        filteredChildren.length === originalChildren.length &&
        filteredChildren.every((child, index) => child === originalChildren[index]);

      if (childrenUnchanged) {
        return node;
      }
      return { ...node, children: filteredChildren };
    }

    // Prune node
    return null;
  };

  return nodes.map(filterNodeRecursively).filter((node): node is WorkspaceNode => node !== null);
}

/**
 * Filters a workspace tree to only include nodes of a specific type and their ancestors.
 *
 * @param nodes - The root WorkspaceNode(s).
 * @param workspaceType - The WorkspaceType to filter for.
 */
export function filterWorkspaceTreeByWorkspaceType(
  nodes: WorkspaceNode[],
  workspaceType: WorkspaceType
): WorkspaceNode[] {
  return filterWorkspaceTree(nodes, (node) => node.type === workspaceType);
}

/**
 * Filters a workspace tree to include only nodes whose displayName matches the search term
 * (case-insensitive) or that have descendants matching the search term.
 *
 * @param nodes - The root node(s).
 * @param searchTerm - The search query.
 */
export function filterWorkspaceTreeBySearchTerm(
  nodes: WorkspaceNode[],
  searchTerm: string
): WorkspaceNode[] {
  const normalizedSearch = (searchTerm ?? "").trim().toLowerCase();
  const fieldsToSearch = ["displayName", "workspaceId"] as const;

  if (!normalizedSearch) {
    return nodes;
  }

  return filterWorkspaceTree(nodes, (node) =>
    fieldsToSearch.some((field) => node[field].toLowerCase().includes(normalizedSearch))
  );
}

/**
 * Checks whether any structured (non-search) filter has a non-default value.
 * Used as an early-exit to skip filtering when no filters are active.
 *
 * @param filters - The current treeview filter state.
 * @returns `true` if at least one filter criterion is set.
 */
function hasActiveFilters(filters: MspTreeviewFilters): boolean {
  return (
    filters.subscriptionTypes.length > 0 ||
    filters.nearExpirationOnly ||
    filters.labels.length > 0 ||
    filters.mspPolicyId !== "" ||
    filters.addons.length > 0
  );
}

/**
 * Builds pre-computed lookup structures from the raw filter state.
 * Called once before tree traversal to hoist all expensive operations
 * (Set construction, date math, array splitting) out of the per-node loop.
 */
function buildFilterLookups(filters: MspTreeviewFilters): StructuredFilterLookups {
  const selectedBundles = new Set<SubscriptionBundle>();
  const selectedModules = new Set<SubscriptionModule>();
  const selectedAddons = new Set<SubscriptionAddon>();

  // Single pass over addons array, splitting into three Sets by type
  for (const addon of filters.addons) {
    switch (addon.type) {
      case SubscriptionsFilterType.BUNDLE:
        selectedBundles.add(addon.name);
        break;
      case SubscriptionsFilterType.MODULE:
        selectedModules.add(addon.name);
        break;
      case SubscriptionsFilterType.ADDON:
        selectedAddons.add(addon.name);
        break;
    }
  }

  return {
    subscriptionTypeSet: new Set<SubscriptionType>(filters.subscriptionTypes),
    labelSet: new Set<string>(filters.labels),
    mspPolicyId: filters.mspPolicyId,
    nearExpirationOnly: filters.nearExpirationOnly,
    // Compute the cutoff date once; null when the filter is off
    expirationCutoff: filters.nearExpirationOnly ? moment.utc().add(7, "days") : null,
    selectedBundles,
    selectedModules,
    selectedAddons,
    hasAddonFilter: filters.addons.length > 0,
  };
}

/**
 * Tests whether a single CHILD workspace node passes all active structured filters.
 * All active filters are AND-ed: the node must satisfy every non-empty criterion.
 * Within each criterion the matching is OR-based (e.g. node matches if it has ANY of the selected labels).
 *
 * Filter criteria:
 * - **subscriptionTypes** — node's plan must be in the selected Set
 * - **nearExpirationOnly** — node must be a TRIAL expiring before the pre-computed cutoff
 * - **labels** — at least one of the node's label IDs must be in the selected Set
 * - **mspPolicyId** — exact match, or "NONE" matches nodes without a policy
 * - **addons** — short-circuiting OR across pre-split bundle/module/addon Sets
 *
 * @param node - The workspace node to test.
 * @param ctx - Pre-computed filter lookups from `buildFilterLookups`.
 * @returns `true` if the node passes all active filter criteria.
 */
function matchesStructuredFilters(node: WorkspaceNode, ctx: StructuredFilterLookups): boolean {
  // 1. Subscription Type (Plan)
  if (ctx.subscriptionTypeSet.size > 0 && !ctx.subscriptionTypeSet.has(node.subscriptionType)) {
    return false;
  }

  // 2. Near Expiration
  if (ctx.nearExpirationOnly) {
    const isTrial = node.subscriptionType === SubscriptionType.TRIAL;
    const hasValidDate = typeof node.trialEndDate === "number";

    if (
      !isTrial ||
      !hasValidDate ||
      !ctx.expirationCutoff ||
      !moment.utc(node.trialEndDate).isBefore(ctx.expirationCutoff)
    ) {
      return false;
    }
  }

  // 3. Labels
  if (ctx.labelSet.size > 0) {
    // Check if there's an intersection between the node's labels and the selected filter labels.
    // Optional chaining (?.) safely handles nodes that might have undefined labels arrays.
    // Set.has() provides an O(1) lookup instead of a nested Array.includes() loop.
    const hasMatchingLabel = node.labels?.some((l) => ctx.labelSet.has(l.id)) ?? false;

    // Prune the node if it doesn't possess at least one of the required labels
    if (!hasMatchingLabel) {
      return hasMatchingLabel;
    }
  }

  // 4. MSP Policy
  if (ctx.mspPolicyId) {
    // Special case: The user specifically wants to filter for nodes that have NO policy assigned
    const isNoneFilter = ctx.mspPolicyId === "NONE";

    // Prune the node if we want "NONE" but it actually has a policy assigned
    if (isNoneFilter && node.mspPolicyId) return false;

    // Prune the node if we want a specific policy ID and it doesn't match exactly
    if (!isNoneFilter && node.mspPolicyId !== ctx.mspPolicyId) return false;
  }

  // 5. Addons / Bundles / Modules
  // If the user selected any bundles, modules, or addons to filter by
  if (ctx.hasAddonFilter) {
    const sub = node.subscription;

    // By chaining these with `||`, JavaScript natively short-circuits.
    // If a bundle matches, it will completely skip the heavier object iteration for modules and addons.
    const matchesAnyAddonFilter =
      // 1. Check if the node has any of the selected bundles in its bundle array
      (ctx.selectedBundles.size > 0 && sub?.bundles?.some((b) => ctx.selectedBundles.has(b))) ||
      // 2. Check if the node has any of the selected modules (requires iterating the modules object and ensuring it's enabled)
      (ctx.selectedModules.size > 0 &&
        Object.entries(sub?.modules ?? {}).some(
          ([name, info]) => ctx.selectedModules.has(name) && info.enabled
        )) ||
      // 3. Check if the node has any of the selected addons (requires iterating modules, then iterating the nested addons array, and ensuring it's enabled)
      (ctx.selectedAddons.size > 0 &&
        Object.values(sub?.modules ?? {}).some((m) =>
          m.addons?.some((a) => ctx.selectedAddons.has(a.name) && a.enabled)
        ));

    // Prune the node if it doesn't possess ANY of the requested subscription add-on items
    if (!matchesAnyAddonFilter) {
      return false;
    }
  }

  // Node passed all active filters
  return true;
}
/**
 * Filters a workspace tree by structured filters (plan, labels, policy, addons).
 *
 * Key behavior:
 * - Only CHILD nodes are filtered by structured filters
 * - Channel nodes always pass through (they are structural grouping nodes)
 * - A channel is retained if any of its descendant CHILD nodes match
 * - Returns the full tree unchanged if no filters are active
 *
 * Performance: pre-computes all filter lookups (Sets, date cutoff, addon splitting)
 * once via `buildFilterLookups` before traversing the tree, so the per-node predicate
 * uses only O(1) Set lookups and pre-computed values.
 *
 * This is the first step of a two-step filtering pipeline used in the treeview tab:
 * 1. `filterWorkspaceTreeByFilters` — structured filters (CHILD-only)
 * 2. `filterWorkspaceTreeBySearchTerm` — search text (all node types)
 *
 * @param nodes - The root WorkspaceNode(s) of the tree.
 * @param filters - The treeview filter state from `useFilters(FilterContext.MSP_TREEVIEW)`.
 * @returns A filtered tree preserving ancestor paths to matching CHILD nodes.
 */
export function filterWorkspaceTreeByFilters(
  nodes: WorkspaceNode[],
  filters: MspTreeviewFilters
): WorkspaceNode[] {
  if (!hasActiveFilters(filters)) {
    return nodes;
  }

  // Pre-compute all filter lookups once before traversing the tree
  const ctx = buildFilterLookups(filters);

  return filterWorkspaceTree(nodes, (node) => {
    // Channels always pass structured filters — only CHILD nodes get filtered
    if (node.type !== WorkspaceType.CHILD) {
      return true;
    }
    return matchesStructuredFilters(node, ctx);
  });
}
